/**
 * ShellSurfaceContext — optional shell-local state that primitives can read
 * to react to surface-level UI affordances (map ↔ camera mode toggle on the
 * Le Vanilla + Map shell, for example).
 *
 * Lives separately from {@link LayoutContext} because:
 *   - LayoutContext is set once at the page level (event/cameras/room).
 *     It would be wrong to bake shell-internal state into it.
 *   - Shells that don't need a surface mode (Le Vanilla, future templates)
 *     simply never install this provider; primitives that read it get the
 *     no-op default and behave as if the feature is unavailable.
 *
 * Usage:
 *   - The shell installs <ShellSurfaceProvider value={{ ... }}> around its
 *     rendered slots.
 *   - Primitives call `useShellSurface()` to read mode + request changes.
 *     If the shell hasn't installed the provider, `requestMode` is a no-op
 *     and `mode` is null.
 */

import { createContext, useContext, type ReactNode } from 'react'

export type ShellSurfaceMode = 'map' | 'camera'

export interface ShellSurfaceContextValue {
  /** Current surface mode. Null when the host shell has no surface concept. */
  mode: ShellSurfaceMode | null
  /** Ask the shell to flip to a different surface mode. No-op by default. */
  requestMode: (mode: ShellSurfaceMode) => void
}

const DEFAULT: ShellSurfaceContextValue = {
  mode: null,
  requestMode: () => undefined,
}

const ShellSurfaceCtx = createContext<ShellSurfaceContextValue>(DEFAULT)

export function ShellSurfaceProvider({
  value,
  children,
}: {
  value: ShellSurfaceContextValue
  children: ReactNode
}) {
  return <ShellSurfaceCtx.Provider value={value}>{children}</ShellSurfaceCtx.Provider>
}

export function useShellSurface(): ShellSurfaceContextValue {
  return useContext(ShellSurfaceCtx)
}
